import { CheckCircle } from '@mui/icons-material';
import { CardContainer } from 'finara-components';

import type { RiskAssessment, RiskCategory } from '@/types/riskAssessment';

interface RiskCategoryCardProps {
  category: RiskCategory;
  assessments: RiskAssessment[];
  onFactorChange: (
    categoryId: string,
    factorId: string,
    optionId: string,
    riskLevel: 'low' | 'medium' | 'high',
    score: number
  ) => void;
}

export function RiskCategoryCard({ category, assessments, onFactorChange }: RiskCategoryCardProps) {
  const completedFactors = assessments.length;
  const totalFactors = category.factors.length;
  const isComplete = completedFactors === totalFactors;

  return (
    <CardContainer>
      <div className="p-6">
        {/* Category Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl text-brand-blue">{category.icon}</span>
            <div>
              <h3 className="text-xl font-bold text-brand-white">{category.name}</h3>
              <p className="text-sm text-muted">{category.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">
              {completedFactors}/{totalFactors}
            </span>
            {isComplete && <CheckCircle className="text-green-600" />}
          </div>
        </div>

        {/* Factors */}
        <div className="space-y-6">
          {category.factors.map(factor => {
            const assessment = assessments.find(a => a.factorId === factor.id);

            return (
              <div key={factor.id} className="border-t border-border pt-6 first:border-t-0 first:pt-0">
                <div className="mb-3">
                  <h4 className="font-semibold text-brand-white mb-1">{factor.name}</h4>
                  <p className="text-sm text-muted">{factor.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {factor.options.map(option => {
                    const isSelected = assessment?.selectedOptionId === option.id;
                    const borderColor =
                      option.riskLevel === 'low'
                        ? 'border-green-300'
                        : option.riskLevel === 'medium'
                          ? 'border-yellow-300'
                          : 'border-red-300';
                    const bgColor =
                      option.riskLevel === 'low'
                        ? 'bg-green-500/10'
                        : option.riskLevel === 'medium'
                          ? 'bg-yellow-500/10'
                          : 'bg-red-500/10';
                    const selectedBorder =
                      option.riskLevel === 'low'
                        ? 'border-green-600'
                        : option.riskLevel === 'medium'
                          ? 'border-yellow-600'
                          : 'border-red-600';

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() =>
                          void onFactorChange(
                            category.id,
                            factor.id,
                            option.id,
                            option.riskLevel,
                            option.score
                          )
                        }
                        className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                          isSelected
                            ? `${selectedBorder} ${bgColor} shadow-md`
                            : `${borderColor} bg-white/5`
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-medium text-brand-white">{option.label}</span>
                          {isSelected && <CheckCircle className="text-brand-blue text-xl" />}
                        </div>
                        <p className="text-xs text-muted">{option.description}</p>
                        <div className="mt-2">
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              option.riskLevel === 'low'
                                ? 'bg-green-500/20 text-green-400'
                                : option.riskLevel === 'medium'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            Riesgo{' '}
                            {option.riskLevel === 'low'
                              ? 'Bajo'
                              : option.riskLevel === 'medium'
                                ? 'Medio'
                                : 'Alto'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CardContainer>
  );
}
